import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { toast } from 'react-toastify';
// import Razorpay from 'razorpay';

export default function Checkout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [address, setAddress] = useState({
    street: '',
    city: '',
    state: '',
    zip: '',
    country: 'India'
  });
  const [paymentMethod, setPaymentMethod] = useState('razorpay');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const fetchCart = async () => {
      try {
        const { data } = await api.get('/cart');
        setCart(data.data);
      } catch (err) {
        console.error('Error fetching cart:', err);
        toast.error('Failed to load cart');
      } finally {
        setLoading(false);
      }
    };

    fetchCart();
  }, [user, navigate]);

  const handleAddressChange = (e) => {
    setAddress({
      ...address,
      [e.target.name]: e.target.value
    });
  };

  const handlePayment = async () => {
  if (!address.street || !address.city || !address.state || !address.zip) {
    toast.error('Please fill all address fields');
    return;
  }

  try {
    setLoading(true);

    // Don't hardcode; use dynamic response from API
    const { data } = await api.post('/checkout/create-order', { address });

    const options = {
      key: data.key, // Your Razorpay key (public)
      amount: data.order.amount, // In paise (e.g., 50000 for ₹500)
      currency: data.order.currency,
      name: 'Dashion',
      description: 'Order Payment',
      image: '/logo.png',
      order_id: data.order.id, // From backend
      handler: async function (response) {
        try {
          await api.post('/checkout/verify-payment', {
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
            address
          });
          console.log("razorpay_order_id", response.razorpay_order_id);
          toast.success('Payment successful!');
          navigate('/orders');
        } catch (err) {
          console.error('Payment verification failed:', err);
          toast.error('Payment verification failed');
        }
      },
      prefill: {
        name: `${user.firstName} ${user.lastName || ''}`,
        email: user.email,
        contact: user.phone || '9999999999'
      },
      notes: {
        address: `${address.street}, ${address.city}, ${address.state}, ${address.zip}`
      },
      theme: {
        color: '#6366f1'
      }
    };

    const razorpayInstance = new window.Razorpay(options);
    razorpayInstance.open();
  } catch (err) {
    console.error('Payment initiation error:', err);
    toast.error(err.response?.data?.message || 'Payment failed');
  } finally {
    setLoading(false);
  }
};


  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-pulse text-pink-600 text-lg">Loading checkout...</div>
    </div>
  );

  if (!cart?.items?.length) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Your cart is empty</h2>
          <button
            onClick={() => navigate('/')}
            className="mt-4 bg-pink-600 hover:bg-pink-700 text-white px-6 py-2 rounded-md transition"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  const subtotal = cart.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const shipping = 0;
  const tax = subtotal * 0.18;
  const total = subtotal + shipping + tax;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Checkout</h1>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Shipping and Payment */}
        <div className="space-y-6">
          {/* Shipping Address */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Shipping Address</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                <input
                  type="text"
                  name="street"
                  value={address.street}
                  onChange={handleAddressChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    name="city"
                    value={address.city}
                    onChange={handleAddressChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    name="state"
                    value={address.state}
                    onChange={handleAddressChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                  <input
                    type="text"
                    name="zip"
                    value={address.zip}
                    onChange={handleAddressChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  <input
                    type="text"
                    name="country"
                    value={address.country}
                    onChange={handleAddressChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                    disabled
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Payment Method */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Payment Method</h2>
            <div className="space-y-3">
              <div className="flex items-center">
                <input
                  type="radio"
                  id="razorpay"
                  name="paymentMethod"
                  value="razorpay"
                  checked={paymentMethod === 'razorpay'}
                  onChange={() => setPaymentMethod('razorpay')}
                  className="h-4 w-4 text-pink-600 focus:ring-pink-500"
                />
                <label htmlFor="razorpay" className="ml-3 block text-sm font-medium text-gray-700">
                  Razorpay (Credit/Debit Cards, UPI, Netbanking)
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="radio"
                  id="cod"
                  name="paymentMethod"
                  value="cod"
                  checked={paymentMethod === 'cod'}
                  onChange={() => setPaymentMethod('cod')}
                  className="h-4 w-4 text-pink-600 focus:ring-pink-500"
                  disabled
                />
                <label htmlFor="cod" className="ml-3 block text-sm font-medium text-gray-400">
                  Cash on Delivery (Coming Soon)
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Order Summary */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 h-fit sticky top-4">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Order Summary</h2>
          
          {/* Cart Items */}
          <div className="space-y-4 mb-6">
            {cart.items.map(item => (
              <div key={item._id} className="flex justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 flex-shrink-0">
                    <img
                      src={item.product?.images?.[0] || 'https://via.placeholder.com/150'}
                      alt={item.product?.name}
                      className="w-full h-full object-cover rounded-md"
                    />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">{item.product?.name}</h3>
                    <p className="text-xs text-gray-500">
                      {item.quantity} × ₹{item.price}
                    </p>
                  </div>
                </div>
                <span className="text-sm font-medium">₹{(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>

          {/* Order Totals */}
          <div className="space-y-3 border-t border-gray-200 pt-4">
            <div className="flex justify-between">
              <span className="text-gray-600">Subtotal</span>
              <span className="font-medium">₹{subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Shipping</span>
              <span className="font-medium">FREE</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Tax (18%)</span>
              <span className="font-medium">₹{tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between border-t border-gray-200 pt-3">
              <span className="text-gray-900 font-semibold">Total</span>
              <span className="text-pink-600 font-bold">₹{total.toFixed(2)}</span>
            </div>
          </div>

          {/* Checkout Button */}
          <button
            onClick={handlePayment}
            disabled={loading}
            className="w-full mt-6 bg-pink-600 hover:bg-pink-700 text-white py-3 rounded-md font-medium transition disabled:opacity-50"
          >
            {loading ? 'Processing...' : 'Proceed to Payment'}
          </button>

          <p className="text-xs text-gray-500 mt-4 text-center">
            Secure checkout with SSL encryption
          </p>
        </div>
      </div>
    </div>
  );
}